export const getRotateToNorthAngleDeg = (azimuth) => {
  // in edifice.json apartment.azimuth is a value in degrees in cartesian coordinates with right hand rule,
  // where 0 degrees is the positive x-axis and the angle increases counterclockwise,
  // meanwhile, in web browser, 0 degrees is the positive y-axis and the angle increases clockwise;
  // Meaning that due north stored in .azimuth is '90';
  const limitedAzimuth = azimuth % 360; // Just in case, to limit the azimuth angle to the range [0, 360]
  // To convert the azimuth angle to the correct angle in web browser, we need to do the following:
  const browserNorthRotation = 90 - limitedAzimuth;
  // To get correct rotation angle of plans in web browser, we need `-1 * (90 - azimuth)`:
  const angle = -1 * browserNorthRotation;
  // We don't want to rotate the plans more than 180 degrees, so we need to limit the angle to the range [-180, 180]:
  const correctedAngle = angle > 180 ? angle - 360 : angle;

  return correctedAngle;
};


/**
 * Converts a number to a zero-based index.
 * @param {number|string} num - The number to convert.
 * @returns {number} The zero-based index.
 */
export const toIndex = (num) => {
    const parsedNum = parseInt(num) || Number(num);

    if(isNaN(parsedNum) || parsedNum < 1) {
        return 0;
    } 

   return  Math.floor(parsedNum) - 1;
};

/**
 * Parses an SVG path string and extracts the coordinates of its vertices.
 * Supports M, m, L, l, H, h, V, v, Z, z commands.
 * For M/m commands, subsequent coordinate pairs are treated as L/l commands.
 * @param {string} pathData - The SVG path data string (e.g., "M10 10 L 20 20 H 30 Z").
 * @returns {Array<Array<number>>} An array of points, where each point is an array [x, y].
 * Returns an empty array if no commands are found or if pathData is invalid.
 */
export const getBoundaryPoints = (pathData) => {
  const commands = pathData.match(/[a-zA-Z][^a-zA-Z]*/g);
  if (!commands) {
    console.warn("No commands found in pathData:", pathData);
    return [];
  }
  let currentPoint = { x: 0, y: 0 };
  const points = [];

  commands.forEach((command) => {
    const type = command[0];
    const valuesString = command.slice(1).trim();
    // Handle commands like Z/z that have no values
    const values = valuesString === "" ? [] : valuesString.split(/[\s,]+/).map(parseFloat);

    switch (type) {
      case "M": // moveto absolute
        if (values.length >= 2) {
          currentPoint = { x: values[0], y: values[1] };
          points.push([currentPoint.x, currentPoint.y]);
          // Subsequent pairs are treated as Lineto
          for (let i = 2; i < values.length; i += 2) {
            if (values[i] !== undefined && values[i+1] !== undefined) {
              currentPoint = { x: values[i], y: values[i+1] };
              points.push([currentPoint.x, currentPoint.y]);
            }
          }
        }
        break;
      case "m": // moveto relative
        if (values.length >= 2) {
          currentPoint = {
            x: currentPoint.x + values[0],
            y: currentPoint.y + values[1],
          };
          points.push([currentPoint.x, currentPoint.y]);
          // Subsequent pairs are treated as lineto
          for (let i = 2; i < values.length; i += 2) {
            if (values[i] !== undefined && values[i+1] !== undefined) {
              currentPoint = {
                x: currentPoint.x + values[i],
                y: currentPoint.y + values[i+1],
              };
              points.push([currentPoint.x, currentPoint.y]);
            }
          }
        }
        break;
      case "L": // lineto absolute
        for (let i = 0; i < values.length; i += 2) {
          if (values[i] !== undefined && values[i+1] !== undefined) {
            currentPoint = { x: values[i], y: values[i+1] };
            points.push([currentPoint.x, currentPoint.y]);
          }
        }
        break;
      case "l": // lineto relative
        for (let i = 0; i < values.length; i += 2) {
          if (values[i] !== undefined && values[i+1] !== undefined) {
            currentPoint = {
              x: currentPoint.x + values[i],
              y: currentPoint.y + values[i+1],
            };
            points.push([currentPoint.x, currentPoint.y]);
          }
        }
        break;
      case "H": // horizontal lineto absolute
        for (let i = 0; i < values.length; i++) {
          if (values[i] !== undefined) {
            currentPoint.x = values[i];
            points.push([currentPoint.x, currentPoint.y]);
          }
        }
        break;
      case "h": // horizontal lineto relative
        for (let i = 0; i < values.length; i++) {
          if (values[i] !== undefined) {
            currentPoint.x += values[i];
            points.push([currentPoint.x, currentPoint.y]);
          }
        }
        break;
      case "V": // vertical lineto absolute
        for (let i = 0; i < values.length; i++) {
          if (values[i] !== undefined) {
            currentPoint.y = values[i];
            points.push([currentPoint.x, currentPoint.y]);
          }
        }
        break;
      case "v": // vertical lineto relative
        for (let i = 0; i < values.length; i++) {
          if (values[i] !== undefined) {
            currentPoint.y += values[i];
            points.push([currentPoint.x, currentPoint.y]);
          }
        }
        break;
      case "Z":
      case "z":
        // Close path: For a list of vertices, we don't add the first point again;
        // If explicit closing point is needed:
        // if (points.length > 0 && subPathInitialPoint) {
        //   points.push([subPathInitialPoint.x, subPathInitialPoint.y]);
        // }
        break;
      default:
        console.warn(`Unsupported path command: ${type}`);
    }
  });

  return points;
};

export const geometryUtils = () => {
  //TODO: move other functions out to own exports
  const calculateAngleFromAzimuth = (azimuth) => -1 * (90 - azimuth);

  const toRadians = (angle) => (angle * Math.PI) / 180;

  const rotatePointClockwise = (x, y, angle, cx, cy) => {
    const radians = toRadians(-angle);
    const cos = Math.cos(radians);
    const sin = Math.sin(radians);
    const nx = cos * (x - cx) - sin * (y - cy) + cx;
    const ny = sin * (x - cx) + cos * (y - cy) + cy;
    return { x: nx, y: ny };
  };

  const lineIntersection = (line1, line2) => {
    const [x1, y1] = line1[0];
    const [x2, y2] = line1[1];
    const [x3, y3] = line2[0];
    const [x4, y4] = line2[1];

    const denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);

    if (denom === 0) return false;

    const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denom;
    const u = -((x1 - x2) * (y1 - y3) - (y1 - y2) * (x1 - x3)) / denom;

    if (t > 0 && t < 1 && u > 0 && u < 1) {
      const intersectionX = x1 + t * (x2 - x1);
      const intersectionY = y1 + t * (y2 - y1);
      return [intersectionX, intersectionY];
    }

    return false;
  };

  const shiftCoordinates = (coordinates, angle, distance) => {
    const radians = toRadians(angle + 90); //90 degrees added to make the shift of 6hours (quarter circle)
    const dx = distance * Math.cos(radians);
    const dy = distance * Math.sin(radians);

    return coordinates.map(([x, y]) => [x + dx, y + dy]);
  };

  const constructParallelogram = (
    vertexArray,
    startIndex,
    endIndex,
    angle,
    distance
  ) => {
    return [
      ...vertexArray.slice(startIndex, endIndex + 1),
      ...shiftCoordinates([vertexArray[endIndex]], angle, distance),
      ...shiftCoordinates([vertexArray[startIndex]], angle, distance),
    ];
  };

  const getGradientCoordinates = (angle) => {
    const radians = toRadians(angle + 270); //+270

    const x = Math.cos(radians);
    const y = Math.sin(radians);

    // Normalize x and y to the range [0, 1]
    const x1 = (x + 1) / 2;
    const y1 = (y + 1) / 2;
    const x2 = (1 - x) / 2;
    const y2 = (1 - y) / 2;

    return { x1, y1, x2, y2 };
  };

  return {
    calculateAngleFromAzimuth,
    toRadians,
    rotatePointClockwise,
    lineIntersection,
    constructParallelogram,
    getGradientCoordinates,
  };
};
